import { IProcessedStyleSet } from 'office-ui-fabric-react';
import * as React from 'react';

import {
  DiagnosticsActionResult,
  GraphNode,
  ISAFActionExecutionResponse,
  NodeType,
  ParamOrVar,
  ReadWriteAction,
  ReadWriteParams,
  SAFActionSchemaResponse
} from '../../models';
import { deserializeToImmutableList } from '../../serialize';
import { createStructuredSelector, IImmutableMap, mconnect } from '../../state/redux';
import { IStyledComponent, styledComponent } from '../../theme';
import { IConnectedComponentProps, INodeListStyles } from '../../types';
import { convertGlobalVarsToObj, getContextVars, _isElmInViewport, _notNullableArray } from '../../utils/utils';
import { getNodeListClassNames, getNodeListStyles } from '../intentGraph.styles';
import { safDiagnosticsCompletedAction, updateGlobalVarsAction } from '../reducers/actions';
import { dispatchDiagnosticsCompletedAction, dispatchSAFActionSchemaUpdate, dispatchSAFActionExecutionUpdate, dispatchReturnControlFromHost } from '../reducers/dispatchers';
import {
  enableScrollToBottomSelector,
  enableScrollToTopSelector,
  globalVarsSelector,
  renderedNodesSelector,
  scrollRefClassNameSelector
} from '../reducers/selectors.redux';

import { Node } from './node';

const mapStateToProps = createStructuredSelector({
  renderedNodes: renderedNodesSelector,
  enableScrollToBottom: enableScrollToBottomSelector,
  enableScrollToTop: enableScrollToTopSelector,
  scrollRefClassName: scrollRefClassNameSelector,
  globalVars: globalVarsSelector
});

const mapDispatchToProps = {
  dispatchDiagnosticsCompletedAction,
  safDiagnosticsCompletedAction,
  dispatchSAFActionSchemaUpdate,
  dispatchSAFActionExecutionUpdate,
  updateGlobalVarsAction,
  dispatchReturnControlFromHost
};

type INodesListProps = ReturnType<typeof mapStateToProps> &
  typeof mapDispatchToProps &
  IStyledComponent<INodeListStyles> &
  IConnectedComponentProps;

class NodesListC extends React.PureComponent<INodesListProps> {
  private containerRef = React.createRef<HTMLDivElement>();
  private renderStartNode = true;

  constructor(props: INodesListProps) {
    super(props);
    if (window.yoda) {
      window.yoda.IntentGraph.Events.onDafDiagnosticsActionCompleted = this.onDafDiagnosticsActionCompleted;
      window.yoda.IntentGraph.Events.onSafDiagnosticsActionCompleted = this.onSafDiagnosticsActionCompleted;
      window.yoda.IntentGraph.Events.onSAFActionSchemaReturned = this.onSAFActionSchemaReturned;
      window.yoda.IntentGraph.Events.onExecuteSAFActionCompleted = this.onExecuteSAFActionCompleted;
      window.yoda.IntentGraph.Events.onReadWrite = this.readWrite;
      window.yoda.IntentGraph.Events.ReturnControl = this.onReturnControl;;
      if (this.props.callbackHandlers.IsInitialized) {
        this.props.callbackHandlers.IsInitialized();
      }
    }
  }

  public componentDidMount = (): void => {
    if (!!this.props.enableScrollToTop) {
      this._scrollToTop();
    } else if (!!this.props.enableScrollToBottom) {
      this._scrollToBottom();
    }
  };

  public componentDidUpdate = (prevProps: INodesListProps): void => {
    if (!!this.props.enableScrollToTop) {
      this._scrollToTop();
    } else if (!!this.props.enableScrollToBottom) {
      this._scrollToBottom();
    }
  };

  public componentWillUnmount = (): void => {
    const renderedNodes = this.props.renderedNodes;
    if (renderedNodes && renderedNodes.size > 0) {
      renderedNodes.forEach((n: IImmutableMap<GraphNode>) => {
        if (n && n.get('timerId') !== undefined) {
          window.clearInterval(n.get('timerId'));
        }
      });
    }
  };

  public _getNodes = (classNames: IProcessedStyleSet<Partial<INodeListStyles>>) => {
    if (!this.props.renderedNodes) {
      return null;
    }
    const nodes: JSX.Element[] = [];
    this.props.renderedNodes.forEach((node: IImmutableMap<GraphNode>, index: number) => {
      // don't show redirectNode if next node has been fetched
      if (
        (node && node.get('type') && node.get('type').toLowerCase() !== NodeType.RedirectNode.toLowerCase()) ||
        index === this.props.renderedNodes.size - 1
      ) {
        const nodeId = node.get('id');
        const uId = node.get('uId');
        const dialogId = node.get('dialogId');
        let className = this.renderStartNode ? classNames.startNodeContainer : classNames.container;
        if (node.get('type').toLowerCase() === NodeType.TextNode) {
          className = classNames.textNodeContainer;
        }
        nodes.push(
          <div className={className} data-key={`graphNode-${index}`} key={`${uId}$graphNode$${index}`}>
            <Node
              componentkey={`${uId}$graphNode$${index}`}
              key={`${uId}$graphNode$${index}`}
              nodeId={nodeId}
              stylesFunc={this.props.stylesFunc}
              callbackHandlers={this.props.callbackHandlers}
              renderStartNode={this.renderStartNode}
              nodeIndex={index}
              uId={uId}
              dialogId={dialogId}
            />
          </div>
        );
      }
    });
    return nodes;
  };

  public render = (): React.ReactNode => {
    const classNames = getNodeListClassNames(this.props.styles, {
      theme: this.props.theme,
      stylesFunc: this.props.stylesFunc
    });
    if (this.props.renderedNodes.count() > 1) {
      this.renderStartNode = false;
    }
    return (
      <div className={classNames.listContainer} ref={this.containerRef}>
        {this._getNodes(classNames)}
      </div>
    );
  };

  private _scrollToTop = () => {
    const lastMessage = this.containerRef.current!.querySelector(
      `div[data-key="graphNodeScrollTop$${this.props.renderedNodes.size - 2}"]`
    ) as HTMLElement;
    if (!!lastMessage && this.props.scrollRefClassName) {
      const elements = document.getElementsByClassName(this.props.scrollRefClassName);
      const host = elements && (elements[0] as HTMLElement);
      if (host) {
        if (
          this.props.callbackHandlers.scrollToTopHandler &&
          typeof this.props.callbackHandlers.scrollToTopHandler === 'function'
        ) {
          this.props.callbackHandlers.scrollToTopHandler(host);
        } else {
          host.scroll({ top: lastMessage.offsetTop - host.offsetTop, left: 0, behavior: 'smooth' });
        }
      }
    }
  };

  private _scrollToBottom = () => {
    const lastMessage = this.containerRef.current!.querySelector(
      `div[data-key="graphNode-${this.props.renderedNodes.size - 1}"]`
    ) as HTMLElement;
    if (!!lastMessage) {
      if (
        this.props.callbackHandlers.scrollToBottomHandler &&
        typeof this.props.callbackHandlers.scrollToBottomHandler === 'function'
      ) {
        this.props.callbackHandlers.scrollToBottomHandler(lastMessage);
      } else {
        lastMessage.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' });
      }
    }
  };

  private onDafDiagnosticsActionCompleted = (nodeId: string, result: DiagnosticsActionResult) => {
    let isError = false;
    if (!result) {
      isError = true;
    }
    this.props.dispatchDiagnosticsCompletedAction(nodeId, result, this.props.callbackHandlers.LogHandler, isError, this.props.callbackHandlers.IsUserSignedIn);
  };

  private onSAFActionSchemaReturned = (result: SAFActionSchemaResponse) => {
    if (
      !!this.props.callbackHandlers.GetSAFActionSchema &&
      typeof this.props.callbackHandlers.GetSAFActionSchema === 'function'
    ) {
      this.props.dispatchSAFActionSchemaUpdate(
        this.props.renderedNodes.last(),
        this.props.callbackHandlers.GetSAFActionSchema,
        this.props.callbackHandlers.LogHandler,
        result
      );
    }
  };

  private onExecuteSAFActionCompleted = (result: ISAFActionExecutionResponse) => {
    if (
      !!this.props.callbackHandlers.ExecuteSAFAction &&
      typeof this.props.callbackHandlers.ExecuteSAFAction === 'function'
    ) {
      this.props.dispatchSAFActionExecutionUpdate(
        this.props.renderedNodes.last(),
        this.props.callbackHandlers.ExecuteSAFAction,
        this.props.callbackHandlers.LogHandler,
        result
      );
    }
  };

  private onSafDiagnosticsActionCompleted = () => {
    this.props.safDiagnosticsCompletedAction();
  };

  private onReturnControl = (success: boolean, uId: string, dialogId: string) => {
    this.props.dispatchReturnControlFromHost(success, uId, dialogId, this.props.callbackHandlers.LogHandler, this.props.callbackHandlers.IsUserSignedIn)
  }

  private readWrite = async (params: ReadWriteParams) => {
    if(params && params.actionType){
      const actionType = params.actionType;
      switch (actionType.toLowerCase()){
        case ReadWriteAction.ReadGlobalVariables.toLowerCase():
          return convertGlobalVarsToObj(this.props.globalVars);
        case ReadWriteAction.ReadContextVariables.toLowerCase():
          const context: string = params.context;
          const contextKeyValueLimit = params.contextKeyValueLimit;
          if(context){
            return getContextVars(this.props.globalVars, context, contextKeyValueLimit);
          }
          break;
        case ReadWriteAction.WriteGlobalVariables.toLowerCase():
          const newGlobalVar = params.parameters;
          if(newGlobalVar){
            await this.props.updateGlobalVarsAction(deserializeToImmutableList(ParamOrVar, newGlobalVar));
          }
          break;
        default:
          break;
      }
    }
  };
}

export const NodesList = mconnect(mapStateToProps, mapDispatchToProps)(styledComponent(NodesListC, getNodeListStyles));
